import { useState } from "react";
import { toast } from "sonner";
import { Eye, EyeOff, UserCheck, Loader2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

export default function SupervisorsBlock({ alert, user, onUpdated }) {
  const [loading, setLoading] = useState(false);
  const supervisors = alert.supervisors || [];
  const isOwner = alert.agent_id === user?.id;
  const isSupervisor = supervisors.includes(user?.id);
  const isFull = supervisors.length >= 2;

  // Fetch supervisor names from agent_snapshot? We don't have team roster client-side.
  // For Phase 3 we display ids shortened + role hint. A proper team lookup is a later optimisation.
  // Simpler: rely on timeline entries to extract names (last supervise_assigned event per user).
  const nameById = {};
  for (const e of alert.timeline || []) {
    if (e.type === "supervise_assigned" && e.by_user_id && e.by_name) {
      nameById[e.by_user_id] = e.by_name;
    }
  }

  const handleSupervise = async () => {
    setLoading(true);
    try {
      const { data } = await api.post(`/alerts/${alert.id}/supervise`);
      toast.success("Vous supervisez ce feu orange.");
      onUpdated(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleRelease = async () => {
    setLoading(true);
    try {
      const { data } = await api.delete(`/alerts/${alert.id}/supervise`);
      toast("Supervision retirée.");
      onUpdated(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="sa-card px-5 py-5" data-testid="supervisors-block">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Eye size={14} className="text-slate-500" />
          <h2 className="text-[13px] font-semibold uppercase tracking-wider text-slate-500">
            Superviseurs
          </h2>
        </div>
        <span
          className={`rounded-full px-2.5 py-0.5 text-[11px] font-bold ${
            isFull
              ? "bg-[var(--sa-blue-soft)] text-[var(--sa-blue-dark)]"
              : "bg-slate-100 text-slate-600"
          }`}
          data-testid="supervisors-count"
        >
          {supervisors.length} / 2
        </span>
      </div>

      {supervisors.length === 0 ? (
        <p className="mt-3 text-[13px] text-slate-500">
          Aucun superviseur pour l'instant. Le rappel, à expiration du délai, sera
          envoyé à toute l'équipe en fallback.
        </p>
      ) : (
        <ul className="mt-3 flex flex-col gap-2" data-testid="supervisors-list">
          {supervisors.map((sid) => {
            const name = nameById[sid] || "Superviseur";
            const initials = name
              .split(" ")
              .filter(Boolean)
              .slice(0, 2)
              .map((w) => w[0]?.toUpperCase())
              .join("");
            return (
              <li
                key={sid}
                className="flex items-center gap-3 rounded-[12px] border border-slate-200 bg-white px-3 py-2.5"
              >
                <div className="flex h-9 w-9 items-center justify-center rounded-full bg-[var(--sa-blue-soft)] text-[12px] font-semibold text-[var(--sa-blue-dark)]">
                  {initials}
                </div>
                <div className="flex min-w-0 flex-1 flex-col">
                  <span className="text-[14px] font-semibold text-slate-900">
                    {name}
                  </span>
                  <span className="text-[11px] text-slate-400">Superviseur actif</span>
                </div>
                <UserCheck size={16} className="text-[var(--sa-blue)]" />
              </li>
            );
          })}
        </ul>
      )}

      <div className="mt-4">
        {isOwner ? (
          <p className="rounded-[10px] bg-slate-50 px-3 py-2 text-[12px] text-slate-500">
            Vous êtes le déclencheur — vous ne pouvez pas superviser votre propre feu.
          </p>
        ) : isSupervisor ? (
          <button
            type="button"
            onClick={handleRelease}
            disabled={loading}
            data-testid="supervise-release"
            className="w-full rounded-[14px] border border-slate-200 bg-white px-5 py-3 text-[14px] font-semibold text-slate-700 transition hover:bg-slate-50 disabled:opacity-60"
          >
            {loading ? (
              <span className="inline-flex items-center justify-center gap-2">
                <Loader2 size={16} className="animate-spin" /> Retrait…
              </span>
            ) : (
              "Me retirer de la supervision"
            )}
          </button>
        ) : isFull ? (
          <p
            className="rounded-[10px] bg-slate-50 px-3 py-2 text-[12px] text-slate-500"
            data-testid="supervision-full"
          >
            Supervision complète (2/2). Les superviseurs recevront le rappel.
          </p>
        ) : (
          <button
            type="button"
            onClick={handleSupervise}
            disabled={loading}
            data-testid="supervise-join"
            className="w-full rounded-[14px] px-5 py-3 text-[14px] font-semibold text-white transition disabled:opacity-60"
            style={{
              background: "#22C55E",
              boxShadow: "0 4px 14px -4px rgba(34,197,94,0.5)",
            }}
          >
            {loading ? (
              <span className="inline-flex items-center justify-center gap-2">
                <Loader2 size={16} className="animate-spin" /> Enregistrement…
              </span>
            ) : (
              "Je supervise"
            )}
          </button>
        )}
      </div>
    </div>
  );
}
